import $ from 'jquery';

import { createFunctionWithTimeout } from './utils';

const pricesEngageActions = {
    'go:estima': 'prices:engage:estima',
    'go:estima_landing_generic': 'prices:engage:estima',
    'go:estima_landing_apartment': 'prices:engage:estima',
    'go:estima_landing_house': 'prices:engage:estima',
    'go:estima_landing_rental': 'prices:engage:estima',
    'go:realtor_page': 'prices:engage:realtor',
    'go:realtor:contact:open': 'prices:engage:realtor',
    'go:past_sale': 'prices:engage:past_sale',
    'go:eval_request_ma': 'prices:engage:eval_request',
    'go:listings': 'prices:engage:listings',
    'go:find_a_pro': 'prices:engage:find_a_pro',
    'go:find_a_pro:city': 'prices:engage:find_a_pro',
    'go:find_a_pro:arrmun': 'prices:engage:find_a_pro',
    'go:find_a_pro:borough': 'prices:engage:find_a_pro',
    'go:find_a_pro:street': 'prices:engage:find_a_pro',
    'go:find_a_pro:address': 'prices:engage:find_a_pro',
    'realtor:show_more': 'prices:engage:realtor',
    'go:past_sales_report': 'prices:engage:past_sales_report',
    'go:rental_price': 'prices:engage:rental_tab',
    'go:sell_price': 'prices:engage:sell_tab',
};

const homeEngageActions = {
    'go:compare': 'home:engage:compare',
    'go:compare:subregion': 'home:engage:compare',
    'go:compare:city': 'home:engage:compare',
    'go:compare:arrmun': 'home:engage:compare',
    'go:estima': 'home:engage:estima',
    'go:prices': 'home:engage:prices',
    'go:dvf_landing': 'home:engage:dvf',
    'go:find_a_pro': 'home:engage:find_a_pro',
    'go:find_best_pro': 'home:engage:find_best_pro',
    'go:ma_pro': 'home:engage:ma_pro',
    'go:sell': 'home:engage:sell',
    'go:how_to_sell': 'home:engage:how_to_sell',
    'go:listings': 'home:engage:listings',
    'go:blog': 'home:engage:blog',
    'go:signin': 'home:engage:signin',
    'go:estima_landing_house': 'home:engage:estima',
    'go:estima_landing_apartment': 'home:engage:estima',
    'go:estima_landing_rental': 'home:engage:estima',
    'go:estima_landing_generic': 'home:engage:estima',
};

const pastSaleEngageActions = {
    'go:realtor_page': 'past_sale:engage:realtor',
};

/**
 * GA wrapper function to handle the case where GA is not (yet) available
 *
 * @private
 */
function _ga(...args) {
    if (!('ga' in window)) {
        window.GoogleAnalyticsConfig.tagsQueue.push(args);
    } else {
        window.ga.apply(window.ga, args);
    }
}

/**
 * Add hit callback on link with data-ua-hit-callback attribute
 *
 * @param {Object} parameters GA event parameters
 * @param {Event} event listened event
 * @param {Element} link clicked link
 * @private
 */
function _uaAddLinkHitCallback(parameters, event, link) {
    const href = link instanceof SVGAElement ? link?.href?.baseVal : link?.href;
    if (href && href.indexOf('#') !== 0 && link.hasAttribute('data-ua-hit-callback')) {
        event.preventDefault();
        return {
            ...parameters,
            hitCallback: createFunctionWithTimeout(() => {
                window.location.href = href;
            }),
        };
    }
    return parameters;
}

/**
 * Parse parameters to skip undefined values
 * @private
 */
function _uaAddParameter(parameters, element, param) {
    const value = element.data(`ua${param.charAt(0).toUpperCase()}${param.slice(1)}`);
    if (typeof value !== 'undefined' && value !== false) {
        return { ...parameters, [param]: value };
    }
    return parameters;
}

/**
 * Function to track users engagement by sending GA events
 * @param       {String} engageCategory    Category of GA engage event
 * @param       {String} engageAction      Action of GA engage event
 * @param       {Object} pageEngageActions Label of GA engage event to send when the associated event action is called
 * @param       {Object} gaParameters      GA event parameters
 * @private     {Function}
 * @return      {Event}                    Send GA event
 */
function _sendEngageEvent(engageCategory, engageAction, pageEngageActions, gaParameters) {
    if (
        gaParameters.eventCategory === engageCategory &&
        gaParameters.eventAction in pageEngageActions
    ) {
        _ga('send', {
            ...gaParameters,
            eventLabel: pageEngageActions[gaParameters.eventAction],
            eventAction: engageAction,
        });
    }
}

/**
 * Send a GA event and its associated engage events if needed
 * @param {Object} parameters GA event parameters
 * @param {String} parameters.hitType hit type
 * @param {String} parameters.eventCategory category (the object that is interacted with)
 * @param {String} parameters.eventAction action (type of interaction)
 * @param {String} parameters.eventLabel label (useful for categorizing events)
 * @param {Function} parameters.hitCallback callback triggered once the hit is done (if provided)
 */
function send({ hitType = 'event', ...params } = {}) {
    const parameters = { hitType, ...params };

    try {
        _ga('send', parameters);
        _sendEngageEvent('Page prix', 'prices:engage', pricesEngageActions, parameters);
        _sendEngageEvent('Home', 'home:engage', homeEngageActions, parameters);
        _sendEngageEvent('Bien Vendu', 'past_sale:engage', pastSaleEngageActions, parameters);
    } catch (e) {
        window.console.error('Error while sending ga with parameters', parameters, e);
    }
}

/**
 * Function to send GA events for a selected DOM element
 * @param  {Element} element selected element
 * @param  {String} hitType GA hit type
 */
function sendForElement(element, hitType = 'event') {
    send({
        hitType,
        eventAction: element.getAttribute('data-ua-event-action'),
        eventLabel: element.getAttribute('data-ua-event-label'),
        eventCategory: element.getAttribute('data-ua-event-category'),
        nonInteraction: Boolean(element.getAttribute('data-ua-non-interaction')),
    });
}

/**
 *
 * @private
 */
function _uaEventHandler(event) {
    const $this = $(this);

    let parameters = {};

    parameters = _uaAddParameter(parameters, $this, 'hitType');

    switch (parameters.hitType) {
        case 'pageview':
            parameters = _uaAddParameter(parameters, $this, 'page');
            parameters = _uaAddParameter(parameters, $this, 'title');
            break;
        case 'event':
            parameters = _uaAddParameter(parameters, $this, 'eventCategory');
            parameters = _uaAddParameter(parameters, $this, 'eventAction');
            parameters = _uaAddParameter(parameters, $this, 'eventLabel');
            parameters = _uaAddParameter(parameters, $this, 'eventValue');
            parameters = _uaAddParameter(parameters, $this, 'nonInteraction');
            parameters = _uaAddLinkHitCallback(parameters, event, this);
            break;
        case 'social':
            parameters = _uaAddParameter(parameters, $this, 'socialNetwork');
            parameters = _uaAddParameter(parameters, $this, 'socialAction');
            parameters = _uaAddParameter(parameters, $this, 'socialTarget');
            break;
        default:
            break;
    }

    send(parameters);
    event.stopPropagation();
}

/**
 * Init tracking on all click and for submit event
 */
function initializeTracking() {
    if (!MA.Context.analyticsEnabled) {
        return;
    }

    // Tags provided by the back-end
    MA.Context.analyticsTags.forEach(tag => {
        if (tag[0] !== 'create' && tag[0] !== 'require') {
            _ga.apply(_ga, tag);
        }
    });
    // Catch elements for Google Universal Analytics tracking
    $(document).on('click', '[data-ua-hit-type]', _uaEventHandler);
    // FixME check and remove
    $(document).on('submit', 'form[data-ua-hit-type]', _uaEventHandler);
}

/**
 * Function to set variables on GA
 * @param  {String} key
 * @param  {String} value
 */
function set(key, value) {
    try {
        _ga('set', key, value);
    } catch (e) {
        window.console.error('Error while setting parameter :', key, e);
    }
}

/**
 * Wrap a function with a max timeout to execute.
 * @param {Function} callback Function to call
 * @param {Integer} timeout Timeout in ms
 * @return {Function} Wrapped function
 */
function functionWithTimeout(callback, timeout = 2000) {
    let called = false;
    let token;

    function wrapped() {
        if (!called) {
            called = true;
            clearTimeout(token);
            callback();
        }
    }

    token = setTimeout(wrapped, timeout);

    return wrapped;
}

export default {
    initializeTracking,
    functionWithTimeout,
    send,
    sendForElement,
    set,
};
